import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Breadcrumb from "../components/Breadcrumb";
import Home from "../components/Home";
import Education from "../components/Education";
import Experience from "../components/Experience";
import Projects from "../components/Projects";
import Skills from "../components/Skills";
import Resume from "../components/Resume";
import References from "../components/References";
import Achievements from "../components/Achievements";
import Certificates from "../components/Certificates";
import Feedback from "../components/Feedback";
import PageNotFound from "../components/PageNotFound";
import RefBTech from "../components/RefBTech";
import RefHS from "../components/RefHS";
import RefeS from "../components/RefeS";
import ViewResume from "../components/ViewResume";
import Test from "../components/Test";
import { AnimatePresence, motion } from "framer-motion";

const Page = () => {
  const [mode, setMode] = useState("");
  const toggle = () => {
    setMode((prev) => (prev === "dark" ? "" : "dark"));
  };

  React.useEffect(() => {
    const saved = window.localStorage.getItem("theme-mode");
    if (saved === "dark") {
      setMode("dark");
    }
  }, []);

  React.useEffect(() => {
    const root = document.documentElement;
    if (mode === "dark") {
      root.classList.add("dark");
      window.localStorage.setItem("theme-mode", "dark");
    } else {
      root.classList.remove("dark");
      window.localStorage.setItem("theme-mode", "");
    }
  }, [mode]);

  // SEO metadata mapping per route
  const SEO = {
    "/": {
      title: "SUBHADIP DAS | Portfolio",
      description:
        "Explore Subhadip Das’s portfolio: skills, projects, experience, education, certificates, references, and resume.",
      label: "Home",
    },
    "/education": {
      title: "Education | Subhadip Das",
      description:
        "Academic background of Subhadip Das including B.Tech and higher secondary details.",
      label: "Education",
    },
    "/experience": {
      title: "Experience | Subhadip Das",
      description:
        "Professional experience and roles held by Subhadip Das.",
      label: "Experience",
    },
    "/projects": {
      title: "Projects | Subhadip Das",
      description:
        "Featured projects including e-commerce, chat platform, file sharing, and more.",
      label: "Projects",
    },
    "/skills": {
      title: "Skills | Subhadip Das",
      description:
        "Technical skills: React, Node, Express, MongoDB, MySQL, and more.",
      label: "Skills",
    },
    "/resume": {
      title: "Resume | Subhadip Das",
      description:
        "View and download the resume of Subhadip Das.",
      label: "Resume",
    },
    "/references": {
      title: "References | Subhadip Das",
      description: "References and recommendations for Subhadip Das.",
      label: "References",
    },
    "/achievements": {
      title: "Achievements | Subhadip Das",
      description: "Achievements and notable milestones of Subhadip Das.",
      label: "Achievements",
    },
    "/certificates": {
      title: "Certificates | Subhadip Das",
      description: "Certificates earned by Subhadip Das.",
      label: "Certificates",
    },
    "/feedback": {
      title: "Feedback | Subhadip Das",
      description: "Provide feedback or contact Subhadip Das.",
      label: "Feedback",
    },
    "/referencebtech": {
      title: "B.Tech Semester Results | Subhadip Das",
      description: "Semester-wise B.Tech results and references.",
      label: "B.Tech",
    },
    "/referencehs": {
      title: "Higher Secondary | Subhadip Das",
      description: "Higher Secondary (XII) result reference.",
      label: "Higher Secondary",
    },
    "/referencesec": {
      title: "Secondary | Subhadip Das",
      description: "Secondary (X) result reference.",
      label: "Secondary",
    },
    "/viewResume": {
      title: "View Resume | Subhadip Das",
      description: "Preview the resume of Subhadip Das.",
      label: "View Resume",
    },
    "/test": {
      title: "Test | Subhadip Das",
      description: "Test page.",
      label: "Test",
    },
  };

  const RouteMeta = () => {
    const location = useLocation();
    const base = "https://subhadipdas.innovexdev.com";
    const meta = SEO[location.pathname] || SEO["/"];
    const canonical = `${base}${location.pathname}`;
    // Breadcrumb structured data
    const breadcrumbItems = [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${base}/`,
      },
    ];
    if (location.pathname !== "/") {
      breadcrumbItems.push({
        "@type": "ListItem",
        position: 2,
        name: meta.label || meta.title,
        item: canonical,
      });
    }

    const breadcrumbJSON = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbItems,
    };

    return (
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={canonical} />
        {/* Open Graph per route */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta
          property="og:image"
          content="https://subhadipdas.innovexdev.com/SubhadipDas.png"
        />
        <meta property="og:image:alt" content="Subhadip Das" />
        <meta property="og:site_name" content="Subhadip Das" />
        {/* Twitter Card per route */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta
          name="twitter:image"
          content="https://subhadipdas.innovexdev.com/SubhadipDas.png"
        />
        <meta name="twitter:image:alt" content="Subhadip Das" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbJSON)}</script>
      </Helmet>
    );
  };

  const ScrollToTop = () => {
    const location = useLocation();
    React.useEffect(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, [location.pathname]);
    return null;
  };

  const Layout = () => {
    const location = useLocation();
    const showShellPaths = [
      "/",
      "/education",
      "/experience",
      "/projects",
      "/skills",
      "/resume",
      "/references",
      "/achievements",
      "/certificates",
      "/feedback",
      "/referencebtech",
      "/referencehs",
      "/referencesec",
    ];
    const showShell = showShellPaths.includes(location.pathname);

    return (
      <div className={`${mode}`}>
        <div className="dark:bg-slate-800">
          {showShell ? <Navbar toggle={toggle} /> : null}
          {location.pathname !== "/viewResume" && location.pathname !== "/test" ? (
            <Breadcrumb />
          ) : null}
          <AnimatePresence mode="wait">
            <motion.div
              className="bg-transparent"
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
           >
              <Routes location={location}>
                <Route exact path="/" element={<Home />} />
                <Route exact path="/education" element={<Education />} />
                <Route exact path="/experience" element={<Experience />} />
                <Route exact path="/projects" element={<Projects />} />
                <Route exact path="/skills" element={<Skills />} />
                <Route exact path="/resume" element={<Resume />} />
                <Route exact path="/references" element={<References />} />
                <Route exact path="/achievements" element={<Achievements />} />
                <Route exact path="/certificates" element={<Certificates />} />
                <Route exact path="/feedback" element={<Feedback />} />
                <Route exact path="/referencebtech" element={<RefBTech />} />
                <Route exact path="/referencehs" element={<RefHS />} />
                <Route exact path="/referencesec" element={<RefeS />} />
                <Route exact path="/viewResume" element={<ViewResume />} />
                <Route exact path="/test" element={<Test />} />
                <Route exact path="/*" element={<PageNotFound />} />
              </Routes>
            </motion.div>
          </AnimatePresence>
          {showShell ? <Footer /> : null}
        </div>
      </div>
    );
  };

  return (
    <>
      <BrowserRouter>
        <RouteMeta />
        <ScrollToTop />
        <Layout />
      </BrowserRouter>
    </>
  );
};

export default Page;
